import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { orderBy } from "lodash";
import Formsy from "formsy-react";
import { Input } from "formsy-react-components";
import { Row, Col } from "react-bootstrap";

import { axios } from "../../lib";
import DropdownSearch from "../common/DropdownSearch";
import PhoneNumberInput from "../common/PhoneNumberInput";

const CustomerForm = ({
  customer,
  codes,
  editing,
  saving,
  onSave,
  onCancel
}) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [state, setState] = useState({});
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await axios.get(`/api/itglue/getOrganizations`);
      const orgOptions = data.data.map((org) => ({
        value: org.id,
        label: org.attributes.name
      }));
      setOrganizations(orgOptions);
    };
    if (!organizations.length) {
      fetchData();
    }
  }, [organizations]);

  const submit = (model) => {
    onSave(model);
  };

  const generateCodeOptions = (entity, field) => {
    const codeOptions = codes
      .filter((code) => code.entity === entity && code.field === field)
      .map((code) => ({ value: code._id, label: code.label }));

    return orderBy(codeOptions, "label");
  };

  const onChange = (field, selected) => {
    setState((prevState) => ({ ...prevState, [field]: selected.value }));
  };

  customer.address = customer.address || {};

  return (
    <Formsy
      className="form"
      onValidSubmit={submit}
      onValid={() => setCanSubmit(true)}
      onInvalid={() => setCanSubmit(false)}
    >
      <Row>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>Customer Info</legend>
          </fieldset>
        </Col>
        <Col md={3} xs={12}>
          <Input
            required={editing}
            name="number"
            label="Customer Number"
            placeholder="Number"
            disabled
            value={customer.number}
            validations={{ maxLength: 50 }}
            validationErrors={{
              maxLength: "You can not type in more than 50 characters"
            }}
          />
        </Col>
        <Col md={5} xs={12}>
          <Input
            required={editing}
            name="name"
            label="Name"
            placeholder="Name"
            disabled
            value={customer.name}
            validations={{ maxLength: 150 }}
            validationErrors={{
              maxLength: "You can not type in more than 150 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            name="alias"
            label="Alias"
            placeholder="Alias"
            disabled={!editing}
            value={customer.alias}
            validations={{ maxLength: 50 }}
            validationErrors={{
              maxLength: "You can not type in more than 50 characters"
            }}
          />
        </Col>
        <Col md={3} xs={12}>
          <PhoneNumberInput
            name="phone"
            label="Phone"
            placeholder="Phone"
            value={customer.phone}
            disabled={!editing}
          />
        </Col>
        <Col md={3} xs={12}>
          <PhoneNumberInput
            name="fax"
            label="Fax"
            placeholder="Fax"
            value={customer.fax}
            disabled={!editing}
          />
        </Col>
        <Col md={3} xs={12}>
          <Input
            name="website"
            label="Website"
            placeholder="Website"
            value={customer.website}
            disabled={!editing}
            validations={{ maxLength: 550 }}
            validationErrors={{
              maxLength: "You can not type in more than 550 characters"
            }}
          />
        </Col>
        <Col md={3} xs={12}>
          <DropdownSearch
            disabled={!organizations.length}
            name="itglue_id"
            label="ITGlue Organization"
            placeholder="ITGlue Organization"
            value={state.itglue_id || customer.itglue_id}
            onChange={onChange}
            data={organizations}
          />
        </Col>
        <Col md={12} xs={12}>
          <fieldset>
            <legend>Primary Address</legend>
          </fieldset>
        </Col>
        <Col md={4} xs={12}>
          <Input
            disabled
            name="address.addr1"
            label="Street Address 1"
            placeholder="Addr 1"
            value={customer.address.addr1}
            validations={{ maxLength: 250 }}
            validationErrors={{
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            disabled
            name="address.addr2"
            label="Street Address 2"
            placeholder="Addr 2"
            value={customer.address.addr2}
            validations={{ maxLength: 250 }}
            validationErrors={{
              maxLength: "You can not type in more than 250 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            disabled
            name="address.city"
            label="City"
            placeholder="City"
            value={customer.address.city}
            validations={{ maxLength: 100 }}
            validationErrors={{
              maxLength: "You can not type in more than 100 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <DropdownSearch
            disabled
            name="address.state"
            label="State"
            placeholder="State"
            value={state["address.state"] || customer.address.state}
            onChange={onChange}
            data={generateCodeOptions("*", "state")}
          />
        </Col>
        <Col md={4} xs={12}>
          <Input
            disabled
            name="address.zip"
            label="Zip"
            placeholder="Zip"
            value={customer.address.zip}
            validations={{ maxLength: 50 }}
            validationErrors={{
              maxLength: "You can not type in more than 50 characters"
            }}
          />
        </Col>
        <Col md={4} xs={12}>
          <DropdownSearch
            disabled
            name="address.country"
            label="Country"
            placeholder="Country"
            value={state["address.country"] || customer.address.country}
            onChange={onChange}
            data={generateCodeOptions("*", "country")}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} xs={12} className="footer">
          <button className="btn btn-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          <input
            className="btn btn-success"
            type="submit"
            disabled={!canSubmit || saving}
            value={saving ? "Saving... " : "Save"}
          />
        </Col>
      </Row>
    </Formsy>
  );
};

CustomerForm.propTypes = {
  customer: PropTypes.object.isRequired,
  codes: PropTypes.array.isRequired,
  editing: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default CustomerForm;
